import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { BsImage } from "react-icons/bs";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";

// carImagesUpload/bikeImagesUpload only accept the FIRST batch of images -
// the controller refuses if the vehicle already has any (see
// "Car images are already uploaded" in CarController.js), so once images
// exist the only way to change one is carImageReplace/bikeImageReplace by
// index. That's why this panel has two completely different modes rather
// than one re-uploadable list.
const VehicleImagesPanel = ({ vehicleType, vehicleId, images, onSaved }) => {
    const initialFilesRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [replacingIndex, setReplacingIndex] = useState(null);
    const replaceInputRefs = useRef({});

    const uploadInitial = async () => {
        const files = initialFilesRef.current?.files;
        if (!files?.length) {
            toast.error("Choose at least 1 image");
            return;
        }
        setUploading(true);
        const fd = new FormData();
        Array.from(files).forEach((file) => fd.append("images", file));
        const response = await Handler({ method: "patch", url: `/admin/${vehicleType}/${vehicleId}/images`, data: fd, formdata: true });
        if (response?.success) {
            toast.success(response?.data?.message || "Images uploaded");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to upload images"));
        }
        setUploading(false);
    };

    const replaceImage = async (index, file) => {
        if (!file) return;
        setReplacingIndex(index);
        const fd = new FormData();
        fd.append("image", file);
        const response = await Handler({ method: "patch", url: `/admin/${vehicleType}/${vehicleId}/images/${index}`, data: fd, formdata: true });
        if (response?.success) {
            toast.success(response?.data?.message || "Image updated");
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update image"));
        }
        setReplacingIndex(null);
    };

    if (!images?.length) {
        return (
            <div>
                <p className="text-muted small">Upload the first set of images (up to 10) for this {vehicleType}. Once uploaded, individual images can be replaced one at a time.</p>
                <div className="d-flex align-items-center gap-2">
                    <input ref={initialFilesRef} type="file" accept="image/*" multiple className="form-control" />
                    <button type="button" className="btn btn-primary btn-sm text-nowrap" onClick={uploadInitial} disabled={uploading}>
                        {uploading ? "Uploading..." : "Upload"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="row g-3">
            {images.map((url, index) => (
                <div key={`${url}-${index}`} className="col-6 col-md-4 col-lg-3">
                    <div className="catalog-logo-lg" style={{ width: "100%", height: 110, borderRadius: 10 }}>
                        {url ? <img src={url} alt="" /> : <BsImage />}
                    </div>
                    <input
                        ref={(el) => { replaceInputRefs.current[index] = el; }}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => replaceImage(index, e.target.files?.[0])}
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary w-100 mt-2"
                        onClick={() => replaceInputRefs.current[index]?.click()}
                        disabled={replacingIndex === index}
                    >
                        {replacingIndex === index ? "Replacing..." : "Replace"}
                    </button>
                </div>
            ))}
        </div>
    );
};

export default VehicleImagesPanel;
